import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Select,
    TextField,
    FormControlLabel,
    Checkbox,
    Typography
    //Typography
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { GridSortModel, GridFilterModel } from "@mui/x-data-grid";
import LayoutAdmin from "../components/LayoutAdmin";
import axiosExt from "axios";
import { User } from "../types"
import { API_BASE_URL } from "../config.ts";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

type UserPayload = {
    userName: string;
    email: string;
    role: string;
    firstName: string | null;
    lastName: string | null;
    phoneNumber: string | null;
    lockoutEnd: string | null;
    emailSignature: string;
    emailSignatureKey: string;
    password?: string;
    emailProviderCategory: string | null;
};

const roles = ["admin", "support"];

const UserManagementPage: React.FC = () => {
    const { t } = useTranslation();

    const [users, setUsers] = useState<User[]>([]);
    const [open, setOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [userName, setUserName] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [emailSignature, setEmailSignature] = useState("");
    const [emailSignatureKey, setEmailSignatureKey] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [emailProviderCategory, setEmailProviderCategory] = useState("");
    const [lockoutEnd, setLockoutEnd] = useState<string | null>(null);
    const [role, setRole] = useState("");

    const [saveError, setSaveError] = useState<string | null>(null);

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<string | null>(null);

    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

    const savedSortKey = "sortUsersList";
    const savedFilterKey = "filterUsersList";

    useEffect(() => {
        const savedSortModel = JSON.parse(localStorage.getItem(savedSortKey) ?? "[]");
        const savedFilterModel = (() => {
            try {
                const parsed = JSON.parse(localStorage.getItem(savedFilterKey) ?? "{}");
                return parsed && parsed.items ? parsed : { items: [] };
            } catch (error) {
                console.error("Error parsing savedFilterKey:", error);
                return { items: [] }; // Fallback
            }
        })();
        setSortModel(savedSortModel);
        setFilterModel(savedFilterModel);

        fetchUsers();
    }, []);

    // Save sortModel changes to localStorage
    const handleSortModelChange = (newModel: GridSortModel) => {
        setSortModel(newModel);
        localStorage.setItem(savedSortKey, JSON.stringify(newModel));
    };

    // Save filterModel changes to localStorage
    const handleFilterModelChange = (newModel: GridFilterModel) => {
        setFilterModel(newModel);
        localStorage.setItem(savedFilterKey, JSON.stringify(newModel));
    };

    const fetchUsers = async () => {
        const token = localStorage.getItem("token");
        const response = await axiosExt.get(`${API_BASE_URL}/users`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        console.log("2 Setting users to:", response.data);
        setUsers(response.data);
    };

    const handleSave = async () => {
        setSaveError(null); // clear previous errors

        if (!selectedUser || (selectedUser && password.trim() !== "")) {
            if (password.trim().length < 6) {
                setSaveError(t("Password must be at least 6 characters long."));
                return;
            }
        }   
        if (!role) {
            setSaveError(t("Role is required."));
            return;
        }
        const token = localStorage.getItem("token");
        const payload: UserPayload = { userName, email, role, password, lockoutEnd, firstName,lastName,phoneNumber,  emailSignature, emailSignatureKey, emailProviderCategory };
        if (!selectedUser || password.trim() !== "") {
            payload.password = password;
        }

        try {

            if (selectedUser) {
                // Avoid sending empty passwords when editing existing users
                await axiosExt.put(`${API_BASE_URL}/users/${selectedUser.id}`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });

            } else {
                // Create User
                await axiosExt.post(`${API_BASE_URL}/users`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }
            setOpen(false);
            setSelectedUser(null);
            setUserName("");
            setEmail("");
            setFirstName("");
            setLastName("");
            setPhoneNumber("");
            setRole("");
            setEmailSignature("");
            setEmailSignatureKey("");
            setPassword("");
            setLockoutEnd(null);
            setEmailProviderCategory("");
            fetchUsers();
        }
        catch (error: unknown) {
            console.error("Save error:", error);
            if (axiosExt.isAxiosError(error)) {
                const responseData = error.response?.data;
                if (Array.isArray(responseData)) {
                    const messages = responseData.map((e: { description?: string }) => e.description ?? String(e));
                    setSaveError(messages.join(", "));
                } else {
                    setSaveError(t("An unexpected error occurred."));
                }
            } else {
                setSaveError(t("An unexpected error occurred."));
            }
        }

    };

    const handleCancel = async () => {
        setOpen(false);
        setSelectedUser(null);
        setUserName("");
        setEmail("");
        setFirstName("");
        setLastName("");
        setPhoneNumber("");
        setRole("");
        setEmailSignature("");
        setEmailSignatureKey("");
        setPassword("");
        setLockoutEnd(null);
        setEmailProviderCategory("");
        fetchUsers();
    };

    const handleEdit = (user: User) => {
        setSelectedUser(user);
        setUserName(user.userName);
        setEmail(user.email);
        setRole(user.role);
        setFirstName(user.firstName ?? "");
        setLastName(user.lastName ?? "");
        setPhoneNumber(user.phoneNumber ?? "");
        setEmailSignature(user.emailSignature);
        setEmailSignatureKey(user.emailSignatureKey);
        setEmailProviderCategory(user.emailProviderCategory || "");
        setLockoutEnd(
            user.lockoutEnd && new Date(user.lockoutEnd) > new Date()
                ? new Date(user.lockoutEnd).toISOString()
                : null
        );
        setPassword("");  // Password not shown for security
        setOpen(true);
    };

    /*const handleDelete = async (id: string) => {
        const token = localStorage.getItem("token");
        await axiosExt.delete(`${API_BASE_URL}/users/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        fetchUsers();
    };*/
    const handleDelete = async (id: string) => {
        try {
            setItemToDelete(id);
            setConfirmDeleteOpen(true);
        } catch (error) {
            console.error("Error deleting email:", error);
        }
    };

    const handleConfirmDelete = async () => {
        if (itemToDelete === null) return;
        try {
            const token = localStorage.getItem("token");
            await axiosExt.delete(`${API_BASE_URL}/users/${itemToDelete}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            fetchUsers();
        } catch (error) {
            console.error("Error deleting:", error);
        } finally {
            setConfirmDeleteOpen(false);
            setItemToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setConfirmDeleteOpen(false);
        setItemToDelete(null);
    };


    const columns: GridColDef[] = [
        { field: "userName", headerName: t("User Name"), width: 150, editable: true },
        { field: "firstName", headerName: t("First Name"), width: 150, editable: true },
        { field: "lastName", headerName: t("Last Name"), width: 150, editable: true },
        { field: "phoneNumber", headerName: t("Phone Number"), width: 150, editable: true },
        { field: "email", headerName: t("Email"), width: 200, editable: true },
        { field: "role", headerName: t("Role"), width: 150, editable: true, type: 'singleSelect',
            valueOptions: ['admin', 'support']
        },
        {
            field: "isLocked",
            headerName: t("Locked"),
            width: 120,
            editable: false,
            renderCell: (params) => {
                const isLocked = params.row.lockoutEnd && new Date(params.row.lockoutEnd) > new Date();
                return isLocked ? t("yes") : t("no");
            }
        },
        {
            field: "actions",
            headerName: t("Actions"),
            width: 300,
            renderCell: (params) => (
                <>
                    <Button variant="contained" onClick={() => handleEdit(params.row)}>
                        {t("Edit")}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleDelete(params.row.id)}
                        style={{ marginLeft: 10 }}
                    >
                        {t("Delete")}
                    </Button>
                </>
            ),
        },
    ];

    return (
        <LayoutAdmin>
            <div style={{ height: "100%" }}>
                <div style={{ width: "100%", display: "flex" }}>
                    <h2>{t("Users")}</h2>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setSelectedUser(null);  // Reset selectedItem to ensure POST request
                            setUserName("");
                            setEmail("");
                            setRole("");
                            setFirstName("");
                            setLastName("");
                            setPhoneNumber("");
                            setEmailSignature("");
                            setEmailSignatureKey("");
                            setPassword(""); 
                            setOpen(true);
                        }}
                        style={{ marginBottom: "20px", marginTop: "20px", marginLeft: "20px" }}
                    >
                        {t("Add New")}
                    </Button>
                </div>

                <Box sx={{ width: "100%"}}>
                    <DataGrid rows={users} columns={columns}
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        filterModel={filterModel}
                        onFilterModelChange={handleFilterModelChange}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 25, page: 0 },
                            },
                        }}
                        pageSizeOptions={[25, 50, 100, 200]}
                    />
                </Box>

                <Dialog open={open} onClose={() => setOpen(false)}>
                    <DialogTitle>{selectedUser ? t("Edit") : t("Add")}</DialogTitle>
                    <DialogContent>
                        {saveError && (
                            <Typography color="error" style={{ marginBottom: "10px" }}>
                                {saveError}
                            </Typography>
                        )}
                        <TextField
                            label={t("User Name")}
                            fullWidth
                            value={userName}
                            onChange={(e) => setUserName(e.target.value)}
                            margin="dense"
                        />
                        <TextField
                            label={t("Email")}
                            fullWidth
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            margin="dense"
                        />
                        <TextField
                            label={t("First Name")}
                            fullWidth
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            margin="dense"
                        />
                        <TextField
                            label={t("Last Name")}
                            fullWidth
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            margin="dense"
                        />
                        <TextField
                            label={t("Phone Number")}
                            fullWidth
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            margin="dense"
                        />
                        <TextField
                            label={t("Password")}
                            type="password"
                            fullWidth
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            margin="dense"
                            helperText={selectedUser ? t("Leave blank to keep existing password") : ""}
                        />
                        <TextField
                            label={t("Email Category")}
                            fullWidth
                            value = { emailProviderCategory }
                            onChange = {(e) => setEmailProviderCategory(e.target.value)}
                        margin="dense"
                        />
                        <Select
                            fullWidth
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            margin="dense"
                            displayEmpty
                        >
                            {roles.map((role) => (
                                <MenuItem key={role} value={role}>
                                    {role}
                                </MenuItem>
                            ))}
                        </Select>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={lockoutEnd ? new Date(lockoutEnd) > new Date() : false}
                                    onChange={(e) => setLockoutEnd(e.target.checked ? new Date(9999, 11, 31).toISOString() : null)}
                                />
                            }
                            label={t("Locked")}
                        />
                        <TextField
                            label={t("Email Signature Key")}
                            fullWidth
                            value={emailSignatureKey}
                            onChange={(e) => setEmailSignatureKey(e.target.value)}
                            margin="dense"
                        />
                        <Typography>{t("Email signature")}</Typography>

                        <div
                            style={{
                                resize: "vertical",
                                overflow: "auto",
                                minHeight: "150px",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "0", // remove spacing if needed
                            }}
                        >
                            <ReactQuill
                                value={emailSignature}
                                onChange={(value) => setEmailSignature(value)}
                                style={{
                                    height: "100%",
                                }}
                            />
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancel}>{t("Cancel")}</Button>
                        <Button onClick={handleSave} variant="contained">
                            {t("Save")}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={confirmDeleteOpen} onClose={handleCancelDelete}>
                    <DialogTitle>{t("Confirm Deletion")}</DialogTitle>
                    <DialogContent>{t("Are you sure you want to delete this record?")}</DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelDelete} color="secondary">{t("no")}</Button>
                        <Button onClick={handleConfirmDelete} color="error">{t("yes")}</Button>
                    </DialogActions>
                </Dialog>
            </div>
        </LayoutAdmin>
    );
};

export default UserManagementPage;
